import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useMemo, useRef, useState } from 'react'
import { AUDIO_BATCH_MAX_ENHANCEMENT_HINTS } from '@kiao-voice/api-contract/batch-transcription'
import { getDictionary, listDictionaries } from '@/features/dictionary/api-client'
import type { DictionaryEntryView, DictionarySummary } from '@/features/dictionary/types'
import type { DictionarySource } from '@kiao-voice/core'
import {
  entriesOfState,
  entryStateIsUsable,
  mergeDictionaryList,
  pruneSelectedIds,
  removedDictionaryIds,
  type DictionaryEntryState,
} from '@/features/dictionary/picker-refresh'

/**
 * A catch-up must always settle: the refocus hook runs one refresh at a time, so
 * a request that never resolves would silently retire this modal's refreshes for
 * the rest of its session. Only the callers a hanging promise would WEDGE are
 * bounded by it — the plain check and the retry link are deliberately left
 * unbounded, because cutting a legitimately slow read off at 8s would fail a
 * dictionary that would have arrived.
 */
const REFRESH_TIMEOUT_MS = 8000

export interface UploadDictionariesOptions {
  /** Every mutation is inert while an upload is in flight. */
  submitting: boolean
  /**
   * The hint cap, read at CALL time rather than captured: checking a dictionary
   * is refused when the expanded list is already full, and that fullness is
   * derived downstream from this hook's own output.
   */
  hintLimitReachedRef: React.RefObject<boolean>
  /** Session guard shared with the rest of the modal. */
  currentSession: () => number
  isCurrentSession: (session: number) => boolean
  /** From the workspace-scoped loader; guards writes across a workspace switch. */
  captureWorkspace: () => () => boolean
  /** A dictionary vanished server-side — dependent state elsewhere must follow. */
  onDictionariesRemoved: (removed: Set<number>) => void
  /** Test seams. */
  fetchDictionaries?: typeof listDictionaries
  fetchDictionaryDetail?: typeof getDictionary
}

export interface UploadDictionaries {
  dictionaries: DictionarySummary[]
  loading: boolean
  listFailed: boolean
  /** Checked ids, in check order — that order IS the dedupe precedence. */
  selectedIds: number[]
  entryStates: Record<number, DictionaryEntryState>
  /** The checked dictionaries' terms, shaped for `expandHints`. */
  sources: DictionarySource[]
  /** Terms not readable yet — Start stays down until they are. */
  termsPending: boolean
  /** At least one checked dictionary could not be read. */
  termsFailed: boolean
  toggleDictionary: (dictionary: DictionarySummary, checked: boolean) => Promise<void>
  loadDictionary: (dictionary: DictionarySummary) => Promise<void>
  handlePickerChange: (values: string[]) => void
  /** Catch up with edits made in the dictionary manager tab. */
  refresh: () => Promise<void>
  /** The workspace loader's two halves. */
  clear: () => void
  loadList: (isCurrent: () => boolean) => Promise<void>
  /** A new modal session: drop the selection and invalidate in-flight reads. */
  resetForOpen: () => void
  /** A dictionary the save dialog just created, starting empty. */
  registerCreatedDictionary: (created: DictionarySummary) => void
  /** Read a dictionary's terms for the save dialog, writing state when it may. */
  ensureEntries: (dictionaryId: number) => Promise<DictionaryEntryView[] | null>
  /** A term the save dialog just filed, folded into the newest known terms. */
  recordEntryAdded: (
    dictionaryId: number,
    created: DictionaryEntryView,
    knownEntries: DictionaryEntryView[]
  ) => void
}

/**
 * The dictionaries a batch ships with: the picker's list, which of them are
 * checked, and each one's terms.
 *
 * CONSTRAINT: call this unconditionally, above any early return the modal makes
 * for its closed state. It mounts hooks of its own, so a conditional call
 * changes the hook order between renders.
 *
 * Four callers write the same entry state — the initial load, the check, the
 * refocus catch-up and the save dialog — and they overlap freely, so every read
 * claims a generation and re-checks it at WRITE time. Only the newest issued
 * read may write; a superseded read simply does nothing.
 */
export function useUploadDictionaries({
  submitting,
  hintLimitReachedRef,
  currentSession,
  isCurrentSession,
  captureWorkspace,
  onDictionariesRemoved,
  fetchDictionaries = listDictionaries,
  fetchDictionaryDetail = getDictionary,
}: UploadDictionariesOptions): UploadDictionaries {
  const [dictionaries, setDictionaries] = useState<DictionarySummary[]>([])
  const [loading, setLoading] = useState(false)
  const [listFailed, setListFailed] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [entryStates, setEntryStates] = useState<Record<number, DictionaryEntryState>>({})

  // --- Read ordering -------------------------------------------------------
  const listRequestRef = useRef({ issued: 0, written: 0, outstanding: 0 })
  const claimListRequest = useCallback(() => {
    const slot = listRequestRef.current
    const generation = ++slot.issued
    slot.outstanding += 1
    let settled = false
    return {
      settle: () => {
        if (settled) return
        settled = true
        slot.outstanding -= 1
      },
      mayWrite: () =>
        generation > slot.written && (generation === slot.issued || slot.outstanding === 0),
      markWritten: () => {
        slot.written = generation
      },
    }
  }, [])

  const entryRequestRef = useRef<
    Record<number, { issued: number; written: number; outstanding: number }>
  >({})
  /**
   * Bumped whenever the picker is reset (modal session, workspace switch).
   * Replacing the slot map is not enough on its own: a request issued before
   * the reset still holds its old slot object in closure and would happily
   * write its pre-reset terms over the new ones.
   */
  const entryEpochRef = useRef(0)
  const claimEntryRequest = useCallback((id: number) => {
    const epoch = entryEpochRef.current
    const slot = (entryRequestRef.current[id] ??= { issued: 0, written: 0, outstanding: 0 })
    const generation = ++slot.issued
    slot.outstanding += 1
    let settled = false
    return {
      /** Call once the read has resolved, whichever way, BEFORE asking below. */
      settle: () => {
        if (settled) return
        settled = true
        slot.outstanding -= 1
      },
      /**
       * Whether this read may write. The newest read owns the outcome — but if
       * it delivered nothing and every other read has already gone, the last
       * one to settle takes over, or the dictionary would sit on a skeleton
       * with nothing left in flight to end it. A reset bumps the epoch, so a
       * request issued before it loses its say even though it still holds the
       * old slot in closure.
       */
      mayWrite: () =>
        epoch === entryEpochRef.current &&
        generation > slot.written &&
        (generation === slot.issued || slot.outstanding === 0),
      /** Whether another read for this dictionary is still on its way. */
      othersOutstanding: () => slot.outstanding > 0,
      markWritten: () => {
        slot.written = generation
      },
    }
  }, [])

  /**
   * Reject a request that never answers, for the callers a hanging promise
   * would WEDGE: the refocus hook runs one catch-up at a time, and the save
   * dialog keeps its button disabled until its handler returns. The plain check
   * and the retry link are deliberately left unbounded — a hung read there only
   * leaves a skeleton, and cutting a legitimately slow read off at 8s would
   * fail a dictionary that would have arrived.
   */
  const withRefreshTimeout = useCallback(<T>(work: Promise<T>): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined
    return Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('dictionary request timed out')),
          REFRESH_TIMEOUT_MS
        )
      }),
    ]).finally(() => clearTimeout(timer))
  }, [])

  // --- Mirrors the async paths read ---------------------------------------
  /** Always the CURRENT reader, so the memoized refresh never calls a stale one. */
  const readEntriesRef = useRef<(id: number, options?: { bounded?: boolean }) => Promise<void>>(
    async () => {}
  )
  const pickerRef = useRef({ dictionaries, selectedIds })
  pickerRef.current = { dictionaries, selectedIds }
  const entryStatesRef = useRef(entryStates)
  entryStatesRef.current = entryStates

  // --- Reads ---------------------------------------------------------------
  /**
   * The one place a dictionary's terms are read and written.
   *
   * There is deliberately no cache: checking a dictionary always re-reads it.
   * Reusing terms fetched earlier in the session is what made the stale-terms
   * bug possible, and every attempt to keep the cache and track its staleness
   * produced a worse failure than the request it saved.
   */
  const readDictionaryEntries = useCallback(
    async (id: number, { bounded = false } = {}) => {
      const session = currentSession()
      const request = claimEntryRequest(id)
      // A dictionary that has terms keeps showing them while they are re-read;
      // anything else goes to the skeleton, so a retry visibly restarts instead
      // of leaving its red "could not be loaded" text sitting there.
      setEntryStates((prev) =>
        prev[id]?.status === 'loaded' || prev[id]?.status === 'loading'
          ? prev
          : { ...prev, [id]: { status: 'loading' } }
      )
      try {
        // See `withRefreshTimeout`: bounded only when the caller would be wedged.
        const read = fetchDictionaryDetail(id)
        const detail = await (bounded ? withRefreshTimeout(read) : read)
        request.settle()
        if (!isCurrentSession(session) || !request.mayWrite()) return
        request.markWritten()
        setEntryStates((prev) => ({
          ...prev,
          [id]: { status: 'loaded', entries: detail.entries },
        }))
      } catch (error) {
        reportHandledException(error, {
          source: 'features/batch-transcription/use-upload-dictionaries.ts',
        })
        // A single group can fail independently. Keep it selected so the user
        // can retry or opt out without losing the rest of their choices.
        console.error('[upload-modal] dictionary entries failed to load', error)
        request.settle()
        if (!isCurrentSession(session) || !request.mayWrite()) return
        // Another read for this dictionary is still on its way and owns the
        // outcome; reporting a failure now would flash an error over terms that
        // are about to arrive.
        if (request.othersOutstanding()) return
        request.markWritten()
        setEntryStates((prev) => {
          const current = prev[id]
          // A re-read that could not reach the server settles back to the terms
          // the user already saw. Blocking a ready upload over a network blip is
          // worse than proceeding with them.
          const next: DictionaryEntryState =
            current?.status === 'loaded' ? current : { status: 'failed' }
          return next === current ? prev : { ...prev, [id]: next }
        })
      }
    },
    [claimEntryRequest, currentSession, fetchDictionaryDetail, isCurrentSession, withRefreshTimeout]
  )
  readEntriesRef.current = readDictionaryEntries

  const loadDictionary = useCallback(
    (dictionary: DictionarySummary) => readDictionaryEntries(dictionary.id),
    [readDictionaryEntries]
  )

  const toggleDictionary = useCallback(
    async (dictionary: DictionarySummary, checked: boolean) => {
      if (submitting) return
      if (!checked) {
        setSelectedIds((prev) => prev.filter((id) => id !== dictionary.id))
        return
      }
      if (
        dictionary.entryCount > AUDIO_BATCH_MAX_ENHANCEMENT_HINTS ||
        (!pickerRef.current.selectedIds.includes(dictionary.id) && hintLimitReachedRef.current)
      ) {
        return
      }
      setSelectedIds((prev) => (prev.includes(dictionary.id) ? prev : [...prev, dictionary.id]))
      await loadDictionary(dictionary)
    },
    [hintLimitReachedRef, loadDictionary, submitting]
  )

  /**
   * A picker change is the COMPLETE new selection, not a delta.
   *
   * The picker moves one option at a time today, but the contract it declares
   * is the whole array — a select-all or clear control moves many at once, so
   * every difference has to be applied, not just the first one found.
   *
   * The full difference is taken against the selection the picker was rendered
   * from, then applied one dictionary at a time through `toggleDictionary`
   * rather than by overwriting the state with `values`. That path is where the
   * guards live (a dictionary larger than the whole hint cap, the cap itself)
   * and where a checked dictionary's terms are read; and because every write in
   * it is a functional `setState`, this only ever moves what the user moved. A
   * refocus catch-up that dropped a deleted dictionary meanwhile therefore
   * stays dropped, where assigning a `values` array built before that response
   * would put it back.
   */
  const handlePickerChange = useCallback(
    (values: string[]) => {
      const requested = [...new Set(values.map(Number))]
      const requestedIds = new Set(requested)
      const byId = new Map(dictionaries.map((entry) => [entry.id, entry]))
      for (const id of selectedIds) {
        if (requestedIds.has(id)) continue
        const dictionary = byId.get(id)
        if (dictionary) void toggleDictionary(dictionary, false)
      }
      // In picker order: the order dictionaries are checked in IS the chip group
      // order and the dedupe precedence between them.
      for (const id of requested) {
        if (selectedIds.includes(id)) continue
        const dictionary = byId.get(id)
        if (dictionary) void toggleDictionary(dictionary, true)
      }
    },
    [dictionaries, selectedIds, toggleDictionary]
  )

  const applyRemoved = useCallback(
    (removed: Set<number>, list: DictionarySummary[]) => {
      setListFailed(false)
      setDictionaries((prev) => mergeDictionaryList(prev, list, removed))
      setSelectedIds((prev) => pruneSelectedIds(prev, removed))
      onDictionariesRemoved(removed)
    },
    [onDictionariesRemoved]
  )

  const loadList = useCallback(
    async (isCurrent: () => boolean) => {
      // Merged, never assigned: a dictionary the user created while this read
      // was in flight is newer than the response and must not be wiped by it.
      const listRequest = claimListRequest()
      const knownAtStart = pickerRef.current.dictionaries
      try {
        const list = await fetchDictionaries()
        listRequest.settle()
        if (!isCurrent() || !listRequest.mayWrite()) return
        listRequest.markWritten()
        // A resolved-but-wrong-shape response must degrade to the same visible
        // state as a rejected request instead of taking down the upload modal.
        if (!Array.isArray(list)) {
          console.error('[upload-modal] dictionary list had an unexpected shape', list)
          setDictionaries([])
          setListFailed(true)
          return
        }
        // Reopening keeps the previous list on screen until this lands, so a
        // dictionary deleted meanwhile can be checked in between. Unchecking it
        // here is the only thing that can: its group would otherwise have no
        // name, no retry and no opt-out, and would hold Start down.
        applyRemoved(removedDictionaryIds(knownAtStart, list), list)
      } catch (error) {
        reportHandledException(error, {
          source: 'features/batch-transcription/use-upload-dictionaries.ts',
        })
        listRequest.settle()
        if (!isCurrent() || !listRequest.mayWrite()) return
        listRequest.markWritten()
        console.error('[upload-modal] dictionary list failed to load', error)
        setDictionaries([])
        setListFailed(true)
      } finally {
        // Not generation-guarded: the picker stays disabled until this clears.
        if (isCurrent()) setLoading(false)
      }
    },
    [applyRemoved, claimListRequest, fetchDictionaries]
  )

  /**
   * Catch up with dictionary edits made elsewhere. The modal links out to the
   * dictionary manager with `target="_blank"`, so the tab the user comes back
   * to would otherwise keep showing — and uploading — the terms as they were
   * when it opened, until the page itself was reloaded.
   *
   * Everything here is silent: this runs unprompted, so a failure keeps the
   * picker exactly as the user left it rather than blanking their selection or
   * raising an error over an upload they are in the middle of setting up. A
   * dictionary that vanished server-side is the one removal allowed, and it is
   * computed against the list as it was when the request STARTED so a
   * dictionary created meanwhile is not mistaken for one.
   */
  const refresh = useCallback(async () => {
    const session = currentSession()
    const isCurrentWorkspace = captureWorkspace()
    const startedWith = pickerRef.current
    const listRequest = claimListRequest()
    const isCurrent = () => isCurrentSession(session) && isCurrentWorkspace()
    const list = await withRefreshTimeout(fetchDictionaries()).finally(listRequest.settle)
    if (!isCurrent() || !listRequest.mayWrite()) return
    if (!Array.isArray(list)) {
      console.error('[upload-modal] refreshed dictionary list had an unexpected shape', list)
      return
    }
    const removed = removedDictionaryIds(startedWith.dictionaries, list)
    // Applied straight away rather than after the entry reads: a dictionary the
    // other tab deleted must leave the selection now, or a slow read elsewhere
    // can hold the removal back long enough for the batch to go out still
    // carrying its terms.
    listRequest.markWritten()
    applyRemoved(removed, list)
    // Re-read every dictionary that is checked RIGHT NOW, not the set this
    // refresh started from: one may have been checked while the list read was
    // in flight, and its terms are just as suspect as the rest. These update in
    // place — no gate.
    await Promise.all(
      pickerRef.current.selectedIds
        .filter((id) => !removed.has(id))
        .map((id) => readEntriesRef.current(id, { bounded: true }))
    )
  }, [
    applyRemoved,
    captureWorkspace,
    claimListRequest,
    currentSession,
    fetchDictionaries,
    isCurrentSession,
    withRefreshTimeout,
  ])

  // --- Save-dialog collaborators ------------------------------------------
  const registerCreatedDictionary = useCallback(
    (created: DictionarySummary) => {
      setDictionaries((prev) => [created, ...prev])
      // A fresh dictionary starts empty; record that so a failed entry write
      // retries INTO it instead of creating another one.
      const createdSlot = claimEntryRequest(created.id)
      createdSlot.settle()
      createdSlot.markWritten()
      setEntryStates((prev) => ({ ...prev, [created.id]: { status: 'loaded', entries: [] } }))
    },
    [claimEntryRequest]
  )

  const ensureEntries = useCallback(
    async (dictionaryId: number) => {
      const known = entryStatesRef.current[dictionaryId]
      if (known?.status === 'loaded') return known.entries
      const session = currentSession()
      const request = claimEntryRequest(dictionaryId)
      // `finally`, not the success path: a rejection here jumps to the caller's
      // catch, and a claim that never settles leaves a phantom outstanding read
      // that silently swallows every later failure for this dictionary.
      const detail = await withRefreshTimeout(fetchDictionaryDetail(dictionaryId)).finally(
        request.settle
      )
      if (!isCurrentSession(session)) return null
      if (request.mayWrite()) {
        request.markWritten()
        setEntryStates((prev) => ({
          ...prev,
          [dictionaryId]: { status: 'loaded', entries: detail.entries },
        }))
      }
      return detail.entries
    },
    [claimEntryRequest, currentSession, fetchDictionaryDetail, isCurrentSession, withRefreshTimeout]
  )

  const recordEntryAdded = useCallback(
    (dictionaryId: number, created: DictionaryEntryView, knownEntries: DictionaryEntryView[]) => {
      // This local append is now the newest truth for the dictionary: a refocus
      // refresh that read it BEFORE the write must not put the pre-save list
      // back and make the word the user just saved disappear. The LIST
      // generation is deliberately left alone — claiming it would also discard
      // that refresh's removal handling, leaving a deleted dictionary checked
      // and still shipping its terms, which is worse than a count that is one
      // behind until the next catch-up.
      const savedSlot = claimEntryRequest(dictionaryId)
      savedSlot.settle()
      savedSlot.markWritten()
      // Base it on whatever is CURRENT: a catch-up may have landed while the
      // POST was in flight, and `knownEntries` predates it. Falling back to that
      // snapshot only when the state has no terms of its own covers the other
      // direction — a read still in flight leaves `loading`, and appending to
      // that would publish the saved word as the dictionary's only term.
      const currentState = entryStatesRef.current[dictionaryId]
      const base = currentState?.status === 'loaded' ? currentState.entries : knownEntries
      // A catch-up may already have picked the term up server-side, along with
      // the count that goes with it. Both decisions read the same base, or the
      // count drifts one ahead and can wrongly push the dictionary past the hint
      // cap that gates re-selecting it.
      const alreadyThere = base.some((entry) => entry.id === created.id)
      const nextEntries = alreadyThere ? base : [...base, created]
      setEntryStates((prev) => ({
        ...prev,
        [dictionaryId]: { status: 'loaded', entries: nextEntries },
      }))
      if (!alreadyThere) {
        // Keep the picker's advertised count honest without a refetch.
        setDictionaries((prev) =>
          prev.map((d) => (d.id === dictionaryId ? { ...d, entryCount: d.entryCount + 1 } : d))
        )
      }
    },
    [claimEntryRequest]
  )

  // --- Lifecycle -----------------------------------------------------------
  const invalidateInFlightReads = useCallback(() => {
    // The slots belong to the session that issued them: a read left in flight
    // by the previous open still counts as outstanding, and would make the new
    // session's failures bail out silently onto a permanent skeleton.
    entryRequestRef.current = {}
    entryEpochRef.current += 1
  }, [])

  const clear = useCallback(() => {
    setDictionaries([])
    setLoading(true)
    setListFailed(false)
    setSelectedIds([])
    setEntryStates({})
    invalidateInFlightReads()
  }, [invalidateInFlightReads])

  const resetForOpen = useCallback(() => {
    setSelectedIds([])
    setEntryStates({})
    setListFailed(false)
    invalidateInFlightReads()
  }, [invalidateInFlightReads])

  const sources = useMemo<DictionarySource[]>(
    () =>
      selectedIds.map((id) => ({
        dictionaryId: id,
        dictionaryName: dictionaries.find((dictionary) => dictionary.id === id)?.name ?? '',
        // Invalid entries stay in the list so `expandHints` can state exactly
        // which terms were not brought into this upload.
        entries: entriesOfState(entryStates[id]),
      })),
    [selectedIds, dictionaries, entryStates]
  )

  // A checked dictionary whose terms have not arrived must gate Start: the
  // expansion would treat it as empty, silently dropping its terms from the
  // payload and letting an over-cap list slip past the 50 gate. A FAILED group
  // gates Start too — submitting would ship the batch without that dictionary's
  // terms while the user believes they ride along. A catch-up deliberately does
  // NOT gate Start; only a first read and a failure do.
  const termsPending = selectedIds.some((id) => !entryStateIsUsable(entryStates[id]))
  const termsFailed = selectedIds.some((id) => entryStates[id]?.status === 'failed')

  return {
    dictionaries,
    loading,
    listFailed,
    selectedIds,
    entryStates,
    sources,
    termsPending,
    termsFailed,
    toggleDictionary,
    loadDictionary,
    handlePickerChange,
    refresh,
    clear,
    loadList,
    resetForOpen,
    registerCreatedDictionary,
    ensureEntries,
    recordEntryAdded,
  }
}
